import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getCompanies, deleteCompany, addCompanyContact, updateCompanyContact, deleteCompanyContact } from '../services/api';
import CompanyForm from './CompanyForm';
import PageHeader from './PageHeader';
import { useToast } from '../context/ToastContext';
import './ClientList.css';
const SEARCH_DEBOUNCE_MS = 400;
function ClientList({ user, canEdit = false }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientTypeTab, setClientTypeTab] = useState('potential');
  const [continentTab, setContinentTab] = useState('');
  const [filters, setFilters] = useState({
    industry: '',
    company_type: '',
    zone: '',
    region: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactCompanyId, setContactCompanyId] = useState(null);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    designation: '',
    phones: [''],
    emails: [''],
    notes: '',
  });
  const [deleteContactConfirm, setDeleteContactConfirm] = useState(null);
  const [contactFormError, setContactFormError] = useState(null);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactDeleting, setContactDeleting] = useState(false);
  const searchDebounceRef = useRef(null);
  const [showStagger, setShowStagger] = useState(false);
  const staggerShownRef = useRef(false);
  const { toast } = useToast();
  const hasActiveFilters = searchTerm || filters.industry || filters.company_type || filters.zone || filters.region || continentTab;
  const anyModalOpen = showContactForm || deleteConfirm || deleteContactConfirm;
  useEffect(() => {
    if (!anyModalOpen) return;
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (deleteContactConfirm) setDeleteContactConfirm(null);
      else if (deleteConfirm) setDeleteConfirm(null);
      else if (showContactForm) setShowContactForm(false);
    };
    document.addEventListener('keydown', handleEscape);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = prevOverflow;
    };
  }, [anyModalOpen, deleteContactConfirm, deleteConfirm, showContactForm]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setFilters({ industry: '', company_type: '', zone: '', region: '' });
    setContinentTab('');
  }, []);

  // Fetch companies
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const isClient = clientTypeTab === 'existing';
      const response = await getCompanies({
        is_client: isClient,
        industry: filters.industry || undefined,
        company_type: filters.company_type || undefined,
        zone: filters.zone || undefined,
        continent: continentTab || undefined,
        region: filters.region || undefined,
        search: searchTerm || undefined,
      });
      if (response.success) {
        setCompanies(response.companies);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientTypeTab, continentTab, filters]);
  useEffect(() => {
    if (!loading && companies.length > 0 && !staggerShownRef.current) {
      staggerShownRef.current = true;
      setShowStagger(true);
    }
  }, [loading, companies.length]);

  useEffect(() => {
    if (!showStagger) return;
    const t = setTimeout(() => setShowStagger(false), 800);
    return () => clearTimeout(t);
  }, [showStagger]);

  const isFirstSearchRef = useRef(true);    
  useEffect(() => {
    if (isFirstSearchRef.current) {
      isFirstSearchRef.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchCompanies();
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCompanies();
  };

  // Handle add new company
  const handleAddNew = () => {
    setEditingCompany(null);
    setShowForm(true);
  };

  // Handle edit company
  const handleEdit = (company) => {
    setEditingCompany(company);
    setShowForm(true);
  };

  // Handle delete company
  const handleDelete = async (companyId) => {
    try {
      const response = await deleteCompany(companyId);
      if (response.success) {
        setDeleteConfirm(null);
        fetchCompanies();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingCompany(null);
    fetchCompanies();
    toast.success('Company saved successfully');
  };
  
  // Toggle row expansion
  const toggleExpand = (companyId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };
  
  // Contact form handlers
  const handleAddContact = (companyId) => {
    setContactCompanyId(companyId);
    setEditingContact(null);
    setContactFormError(null);
    setContactFormData({
      name: '',
      designation: '',
      phones: [''],
      emails: [''],
      notes: '',
    });
    setShowContactForm(true);
  };

  const handleEditContact = (companyId, contact) => {
    setContactCompanyId(companyId);
    setEditingContact(contact);
    setContactFormData({
      name: contact.name || '',
      designation: contact.designation || '',
      phones: contact.phones && contact.phones.length > 0 ? [...contact.phones] : [''],
      emails: contact.emails && contact.emails.length > 0 ? [...contact.emails] : [''],
      notes: contact.notes || '',
    });
    setContactFormError(null);
    setShowContactForm(true);
  };

  const handleContactFormChange = (field, value) => {
    setContactFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const addPhoneField = () => {
    setContactFormData(prev => ({
      ...prev,
      phones: [...prev.phones, ''],
    }));
  };
  
  const removePhoneField = (index) => {
    setContactFormData(prev => ({
      ...prev,
      phones: prev.phones.filter((_, i) => i !== index),
    }));
  };
  
  const updatePhone = (index, value) => {
    setContactFormData(prev => ({
      ...prev,
      phones: prev.phones.map((p, i) => i === index ? value : p),
    }));
  };
  
  const addEmailField = () => {
    setContactFormData(prev => ({
      ...prev,
      emails: [...prev.emails, ''],
    }));
  };
  
  const removeEmailField = (index) => {
    setContactFormData(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index),
    }));
  };
  
  const updateEmail = (index, value) => {
    setContactFormData(prev => ({
      ...prev,
      emails: prev.emails.map((e, i) => i === index ? value : e),
    }));
  };
  
  const handleSaveContact = async (e) => {
    e.preventDefault();
    setContactFormError(null);
    
    const contactData = {
      name: contactFormData.name.trim(),
      designation: contactFormData.designation.trim(),
      phones: contactFormData.phones.filter(p => p.trim() !== ''),
      emails: contactFormData.emails.filter(e => e.trim() !== ''),
      notes: contactFormData.notes.trim(),
    };
    
    if (!contactData.name) {
      setContactFormError('Contact name is required.');
      return;
    }
    
    try {
      setContactSaving(true);
      let response;
      if (editingContact) {
        response = await updateCompanyContact(contactCompanyId, editingContact.id, contactData);
      } else {
        response = await addCompanyContact(contactCompanyId, contactData);
      }
      
      if (response && response.success) {
        setShowContactForm(false);
        setContactFormError(null);
        fetchCompanies();
        toast.success(editingContact ? 'Contact updated' : 'Contact added');
      } else {
        setContactFormError(response?.error || 'Failed to save contact.');
      }
    } catch (err) {
      const message = err?.data?.error || err?.message || 'Failed to save contact.';
      setContactFormError(message);
      setError(message);
    } finally {
      setContactSaving(false);
    }
  };
  
  const handleDeleteContact = async () => {
    if (!deleteContactConfirm) return;
    
    try {
      setContactDeleting(true);
      const response = await deleteCompanyContact(
        deleteContactConfirm.companyId,
        deleteContactConfirm.contactId
      );
      if (response && response.success) {
        setDeleteContactConfirm(null);
        fetchCompanies();
      } else {
        setError(response?.error || 'Failed to delete contact.');
      }
    } catch (err) {
      const message = err?.data?.error || err?.message || 'Failed to delete contact.';
      setError(message);
    } finally {
      setContactDeleting(false);
    }
  };

  // Render company type badge
  const renderTypeBadge = (type) => {
    const colors = {
      psu: '#2563eb',
      private: '#059669',
      mnc: '#7c3aed',
      joint_venture: '#d97706',
    };
    return (
      <span 
        className="type-badge" 
        style={{ backgroundColor: colors[type] || '#6b7280' }}
      >
        {type === 'psu' ? 'PSU/Govt' : type === 'mnc' ? 'MNC' : type?.replace('_', ' ')}
      </span>
    );
  };

  const getPotentialFlagClass = (company) => {
    if (!company || company.is_client) return '';
    const flag = company.potential_flag || company.potentialFlag || company.flag_color || company.flagColor;
    if (!flag) return '';

    const normalized = String(flag).toLowerCase();
    if (normalized === 'red' || normalized === 'hot') return 'potential-flag-red';
    if (normalized === 'yellow' || normalized === 'amber' || normalized === 'warm') return 'potential-flag-yellow';
    if (normalized === 'green' || normalized === 'cold') return 'potential-flag-green';
    return '';
  };

  const CONTINENT_TABS = [
    { id: '', label: 'All Continents' },
    { id: 'asia', label: 'Asia' },
    { id: 'europe', label: 'Europe' },
    { id: 'north_america', label: 'North America' },
    { id: 'south_america', label: 'South America' },
    { id: 'africa', label: 'Africa' },
    { id: 'oceania', label: 'Oceania' },
    { id: 'middle_east', label: 'Middle East' },
  ];

  return (
    <div className="client-list-container">
      <PageHeader
        className="client-list-header"
        title="Client List"
        actions={canEdit && (
          <button className="add-btn" onClick={handleAddNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Company
          </button>
        )}
      />

      {/* Main tabs: Potential vs Existing Clients */}
      <div className="client-type-tabs">
        <button
          type="button"
          className={`client-tab ${clientTypeTab === 'potential' ? 'active' : ''}`}
          onClick={() => setClientTypeTab('potential')}
        >
          Potential Clients
        </button>
        <button
          type="button"
          className={`client-tab ${clientTypeTab === 'existing' ? 'active' : ''}`}
          onClick={() => setClientTypeTab('existing')}
        >
          Existing Clients
        </button>
      </div>

      {/* Continent sub-tabs */}
      <div className="continent-tabs">
        {CONTINENT_TABS.map((tab) => (
          <button
            key={tab.id || 'all'}
            type="button"
            className={`continent-tab ${continentTab === tab.id ? 'active' : ''}`}
            onClick={() => setContinentTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="filters-section">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by company name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>

        <div className="filter-group">
          {hasActiveFilters && (
            <button type="button" className="clear-filters-btn" onClick={clearFilters}>
              Clear filters
            </button>
          )}
          <select
            value={filters.industry}
            onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
          >
            <option value="">All Industries</option>
            <option value="power">Power</option>
            <option value="epc">EPC</option>
            <option value="refinery">Refinery</option>
            <option value="renewable_energy">Renewable Energy</option>
            <option value="manufacturing">Manufacturing</option>
            <option value="oil_gas">Oil & Gas</option>
            <option value="infrastructure">Infrastructure</option>
            <option value="steel">Steel</option>
            <option value="cement">Cement</option>
            <option value="chemicals">Chemicals</option>
            <option value="other">Other</option>
          </select>

          <select
            value={filters.company_type}
            onChange={(e) => setFilters({ ...filters, company_type: e.target.value })}
          >
            <option value="">All Types</option>
            <option value="psu">PSU / Government</option>
            <option value="private">Private</option>
            <option value="mnc">MNC</option>
            <option value="joint_venture">Joint Venture</option>
          </select>

          <select
            value={filters.zone}
            onChange={(e) => setFilters({ ...filters, zone: e.target.value })}
          >
            <option value="">All Zones</option>
            <option value="north">North</option>
            <option value="south">South</option>
            <option value="east">East</option>
            <option value="west">West</option>
            <option value="central">Central</option>
            <option value="pan_india">Pan India</option>
          </select>

          <select
            value={filters.region}
            onChange={(e) => setFilters({ ...filters, region: e.target.value })}
          >
            <option value="">All Regions</option>
            <option value="south_asia">South Asia</option>
            <option value="southeast_asia">Southeast Asia</option>
            <option value="east_asia">East Asia</option>
            <option value="central_asia">Central Asia</option>
            <option value="western_asia">Western Asia</option>
            <option value="western_europe">Western Europe</option>
            <option value="eastern_europe">Eastern Europe</option>
            <option value="northern_europe">Northern Europe</option>
            <option value="southern_europe">Southern Europe</option>
            <option value="north_america">North America</option>
            <option value="latin_america">Latin America & Caribbean</option>
            <option value="middle_east">Middle East</option>
            <option value="north_africa">North Africa</option>
            <option value="subsaharan_africa">Sub-Saharan Africa</option>
            <option value="oceania">Oceania</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State - skeleton table */}
      {loading ? (
        <div className="companies-table-wrapper">
          <table className="companies-table table-skeleton">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Company Name</th>
                <th>Abbr.</th>
                <th>Industry</th>
                <th>Type</th>
                <th>Location</th>
                <th>Continent</th>
                <th>Region</th>
                <th>Zone</th>
                <th>Contacts</th>
                <th>Links</th>
                {canEdit && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <tr key={i}>
                  <td><span className="skeleton-box" style={{ width: 28, height: 28 }} /></td>
                  <td><span className="skeleton-box" style={{ width: '80%' }} /></td>
                  <td><span className="skeleton-box" style={{ width: 36 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 70 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 60 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 90 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 64 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 80 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 50 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 48 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 64 }} /></td>
                  {canEdit && <td><span className="skeleton-box" style={{ width: 72 }} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : companies.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="3" width="20" height="18" rx="2"></rect>
            <line x1="9" y1="3" x2="9" y2="21"></line>
          </svg>
          <h3>No companies found</h3>
          <p>
            {hasActiveFilters
              ? 'No companies match your filters. Try clearing filters or adjusting your criteria.'
              : canEdit
                ? 'Get started by adding your first company'
                : 'No companies have been added yet'}
          </p>
          {hasActiveFilters && (
            <button onClick={clearFilters}>Clear filters</button>
          )}
          {canEdit && !hasActiveFilters && (
            <button onClick={handleAddNew}>Add Company</button>
          )}
        </div>
      ) : (
        /* Companies Table */
        <div className="companies-table-wrapper">
          <table className="companies-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }} scope="col" aria-label="Expand" />
                <th scope="col">Company Name</th>
                <th scope="col">Abbr.</th>
                <th scope="col">Industry</th>
                <th scope="col">Type</th>
                <th scope="col">Location</th>
                <th scope="col">Continent</th>
                <th scope="col">Region</th>
                <th scope="col">Zone</th>
                <th scope="col">Contacts</th>
                <th scope="col">Links</th>
                {canEdit && <th scope="col">Actions</th>}
              </tr>
            </thead>
            <tbody className={showStagger ? 'stagger-rows' : ''}>
              {companies.map((company, index) => (
                <React.Fragment key={company.id}>
                  <tr
                    style={showStagger ? { animationDelay: `${index * 0.03}s` } : undefined}
                    className={`${company.is_client ? 'is-client-row' : ''} ${expandedRows.has(company.id) ? 'expanded-row' : ''} ${clientTypeTab === 'potential' ? getPotentialFlagClass(company) : ''}`}
                  >
                    <td className="expand-cell">
                      <button 
                        type="button"
                        className={`expand-btn ${expandedRows.has(company.id) ? 'expanded' : ''}`}
                        onClick={() => toggleExpand(company.id)}
                        title={expandedRows.has(company.id) ? 'Collapse' : 'Expand'}
                        aria-label={expandedRows.has(company.id) ? 'Collapse row' : 'Expand row'}
                        aria-expanded={expandedRows.has(company.id)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </button>
                    </td>
                    <td className="company-name">
                      {company.name}
                      {company.is_client && <span className="client-badge">Client</span>}
                    </td>
                    <td className="abbreviation-cell">{company.abbreviation || '-'}</td>
                    <td>{company.industry_display}</td>
                    <td>{renderTypeBadge(company.company_type)}</td>
                    <td>{company.head_office_location || '-'}</td>
                    <td>{company.continent_display || '-'}</td>
                    <td>{company.region_display || '-'}</td>
                    <td>{company.zone_display || '-'}</td>
                    <td className="contacts-count">
                      <button
                        type="button"
                        className="contacts-count-btn"
                        onClick={() => toggleExpand(company.id)}
                        title="View contacts"
                        aria-label={`${company.contacts?.length || 0} contacts, click to expand`}
                      >
                        <span className="count-badge">
                          {company.contacts?.length || 0}
                        </span>
                      </button>
                    </td>
                    <td className="links-cell">
                      {company.website && (
                        <a href={company.website} target="_blank" rel="noopener noreferrer" title="Website">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="2" y1="12" x2="22" y2="12"></line>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                          </svg>
                        </a>
                      )}
                      {company.linkedin_url && (
                        <a href={company.linkedin_url} target="_blank" rel="noopener noreferrer" title="LinkedIn">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                          </svg>
                        </a>
                      )}
                    </td>
                    {canEdit && (
                      <td className="actions-cell">
                        <button 
                          type="button"
                          className="edit-btn" 
                          onClick={() => handleEdit(company)}
                          title="Edit company"
                          aria-label="Edit company"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button 
                          type="button"
                          className="delete-btn" 
                          onClick={() => setDeleteConfirm(company)}
                          title="Delete company"
                          aria-label="Delete company"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </td>
                    )}
                  </tr>
                  
                  {/* Expanded Details Row */}
                  {expandedRows.has(company.id) && (
                    <tr className="expanded-details-row">
                      <td colSpan={canEdit ? 12 : 11}>
                        <div className="expanded-content">
                          {/* Company Details Section */}
                          <div className="details-section">
                            <h4>Company Details</h4>
                            <div className="details-grid">
                              <div className="detail-item">
                                <span className="detail-label">Full Name</span>
                                <span className="detail-value">{company.name}</span>
                              </div>
                              {company.abbreviation && (
                                <div className="detail-item">
                                  <span className="detail-label">Abbreviation</span>
                                  <span className="detail-value">{company.abbreviation}</span>
                                </div>
                              )}
                              <div className="detail-item">
                                <span className="detail-label">Industry</span>
                                <span className="detail-value">{company.industry_display}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Type</span>
                                <span className="detail-value">{company.company_type_display}</span>
                              </div>
                              {company.head_office_location && (
                                <div className="detail-item">
                                  <span className="detail-label">Head Office</span>
                                  <span className="detail-value">{company.head_office_location}</span>
                                </div>
                              )}
                              {company.continent_display && (
                                <div className="detail-item">
                                  <span className="detail-label">Continent</span>
                                  <span className="detail-value">{company.continent_display}</span>
                                </div>
                              )}
                              {company.region_display && (
                                <div className="detail-item">
                                  <span className="detail-label">Region</span>
                                  <span className="detail-value">{company.region_display}</span>
                                </div>
                              )}
                              {company.zone_display && (
                                <div className="detail-item">
                                  <span className="detail-label">Zone</span>
                                  <span className="detail-value">{company.zone_display}</span>
                                </div>
                              )}
                              {company.website && (
                                <div className="detail-item">
                                  <span className="detail-label">Website</span>
                                  <a href={company.website} target="_blank" rel="noopener noreferrer" className="detail-link">
                                    {company.website}
                                  </a>
                                </div>
                              )}
                              {company.linkedin_url && (
                                <div className="detail-item">
                                  <span className="detail-label">LinkedIn</span>
                                  <a href={company.linkedin_url} target="_blank" rel="noopener noreferrer" className="detail-link">
                                    {company.linkedin_url}
                                  </a>
                                </div>
                              )}
                            </div>
                            {company.about && (
                              <div className="about-section">
                                <span className="detail-label">About</span>
                                <p className="about-text">{company.about}</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Contacts Section */}
                          <div className="contacts-section">
                            <div className="contacts-header">
                              <h4>Contacts ({company.contacts?.length || 0})</h4>
                              {canEdit && (
                                <button 
                                  className="add-contact-btn"
                                  onClick={() => handleAddContact(company.id)}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                  </svg>
                                  Add Contact
                                </button>
                              )}
                            </div>
                            
                            {company.contacts && company.contacts.length > 0 ? (
                              <div className="contacts-grid">
                                {company.contacts.map((contact) => (
                                  <div key={contact.id} className="contact-card">
                                    <div className="contact-header">
                                      <div className="contact-avatar">
                                        {contact.name?.charAt(0)?.toUpperCase() || 'C'}
                                      </div>
                                      <div className="contact-info">
                                        <span className="contact-name">{contact.name}</span>
                                        {contact.designation && (
                                          <span className="contact-designation">{contact.designation}</span>
                                        )}
                                      </div>
                                      {canEdit && (
                                        <div className="contact-actions">
                                          <button 
                                            className="contact-edit-btn"
                                            onClick={() => handleEditContact(company.id, contact)}
                                            title="Edit Contact"
                                          >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                          </button>
                                          <button 
                                            className="contact-delete-btn"
                                            onClick={() => setDeleteContactConfirm({
                                              companyId: company.id,
                                              contactId: contact.id,
                                              contactName: contact.name
                                            })}
                                            title="Delete Contact"
                                          >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <polyline points="3 6 5 6 21 6"></polyline>
                                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            </svg>
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    <div className="contact-details">
                                      {contact.phones && contact.phones.length > 0 && (
                                        <div className="contact-detail-row">
                                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                          </svg>
                                          <div className="contact-values">
                                            {contact.phones.map((phone, idx) => (
                                              <a key={idx} href={`tel:${phone}`} className="phone-link">{phone}</a>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {contact.emails && contact.emails.length > 0 && (
                                        <div className="contact-detail-row">
                                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                            <polyline points="22,6 12,13 2,6"></polyline>
                                          </svg>
                                          <div className="contact-values">
                                            {contact.emails.map((email, idx) => (
                                              <a key={idx} href={`mailto:${email}`} className="email-link">{email}</a>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {contact.notes && (
                                        <div className="contact-notes">
                                          <span className="notes-label">Notes:</span>
                                          <span className="notes-text">{contact.notes}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="no-contacts">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                  <circle cx="9" cy="7" r="4"></circle>
                                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                                <p>No contacts added yet</p>
                                {canEdit && (
                                  <button onClick={() => handleAddContact(company.id)}>
                                    Add First Contact
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Total Count */}
      {!loading && companies.length > 0 && (
        <div className="table-footer">
          <span>Total: {companies.length} companies</span>
        </div>
      )}

      {/* Company Form Modal */}
      {showForm && (
        <CompanyForm
          company={editingCompany}
          onSuccess={handleFormSuccess}
          onClose={() => {
            setShowForm(false);
            setEditingCompany(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <h3>Delete Company</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?</p>
            <p className="warning">This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn" 
                onClick={() => handleDelete(deleteConfirm.id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="modal-overlay">
          <div className="contact-form-modal">
            <div className="modal-header">
              <h3>{editingContact ? 'Edit Contact' : 'Add Contact'}</h3>
              <button type="button" className="close-modal-btn" onClick={() => { setShowContactForm(false); setContactFormError(null); }} disabled={contactSaving}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={handleSaveContact}>
              {contactFormError && (
                <div className="error-message">
                  {contactFormError}
                  <button type="button" onClick={() => setContactFormError(null)} aria-label="Dismiss">×</button>
                </div>
              )}
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={contactFormData.name}
                  onChange={(e) => handleContactFormChange('name', e.target.value)}
                  placeholder="Contact person name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Designation</label>
                <input
                  type="text"
                  value={contactFormData.designation}
                  onChange={(e) => handleContactFormChange('designation', e.target.value)}
                  placeholder="e.g., Director (Technical)"
                />
              </div>
              
              <div className="form-group">
                <label>Phone Numbers</label>
                {contactFormData.phones.map((phone, index) => (
                  <div key={index} className="dynamic-field">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => updatePhone(index, e.target.value)}
                      placeholder="Phone number"
                    />
                    {contactFormData.phones.length > 1 && (
                      <button 
                        type="button" 
                        className="remove-field-btn"
                        onClick={() => removePhoneField(index)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="add-field-btn" onClick={addPhoneField}>
                  + Add Phone
                </button>
              </div>
              
              <div className="form-group">
                <label>Email Addresses</label>
                {contactFormData.emails.map((email, index) => (
                  <div key={index} className="dynamic-field">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => updateEmail(index, e.target.value)}
                      placeholder="Email address"
                    />
                    {contactFormData.emails.length > 1 && (
                      <button 
                        type="button" 
                        className="remove-field-btn"
                        onClick={() => removeEmailField(index)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="add-field-btn" onClick={addEmailField}>
                  + Add Email
                </button>
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={contactFormData.notes}
                  onChange={(e) => handleContactFormChange('notes', e.target.value)}
                  placeholder="Additional notes about this contact"
                  rows="3"
                />
              </div>
              
              <div className="modal-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowContactForm(false)} disabled={contactSaving}>
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={contactSaving}>
                  {contactSaving ? 'Saving...' : (editingContact ? 'Update Contact' : 'Add Contact')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete Contact Confirmation Modal */}
      {deleteContactConfirm && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <h3>Delete Contact</h3>
            <p>Are you sure you want to delete <strong>{deleteContactConfirm.contactName}</strong>?</p>
            <p className="warning">This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                type="button"
                className="cancel-btn" 
                onClick={() => setDeleteContactConfirm(null)}
                disabled={contactDeleting}
              >
                Cancel
              </button>
              <button 
                type="button"
                className="confirm-delete-btn" 
                onClick={handleDeleteContact}
                disabled={contactDeleting}
              >
                {contactDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientList;
